import { Trans } from "@lingui/macro";
import { useEffect, useMemo, useState } from "react";
import { ethers } from "ethers";

import { BESC } from "config/chains";
import { USD_DECIMALS } from "config/factors";
import { useMoneyPrice, useTotalMoneyStaked, useVolumeData } from "domain/legacy";
import { useV1FeesInfo } from "domain/stats";
import { bigMath } from "lib/bigmath";
import { useChainId } from "lib/chains";
import { MLP_DECIMALS, MONEY_DECIMALS } from "lib/legacy";
import { expandDecimals, formatAmount, formatAmountHuman } from "lib/numbers";
import { sumBigInts } from "lib/sumBigInts";
import useWallet from "lib/wallets/useWallet";
import { getFormattedFeesDuration } from "./getFormattedFeesDuration";
import type { ChainStats } from "./useDashboardChainStatsMulticall";
import { useInfoTokens } from "domain/tokens";
import StatsTooltipRow from "components/StatsTooltip/StatsTooltipRow";
import TooltipComponent from "components/Tooltip/Tooltip";

export function OverviewCard({ statsBesc }: { statsBesc?: ChainStats }) {
  const { active, signer } = useWallet();
  const { chainId } = useChainId();

  const { infoTokens } = useInfoTokens(signer, BESC, active);

  const totalLongOiRaw = Object.values(infoTokens)
    .filter((t) => !t.isStable && !t.isNative)
    // Synthetic long OI lives in globalLongSize (guaranteedUsd is 0 for synths)
    .reduce((sum, t) => sum + (t.globalLongSize ?? t.guaranteedUsd ?? 0n), 0n);

  const totalShortOiRaw = Object.values(infoTokens)
    .filter((t) => !t.isStable && !t.isNative)
    .reduce((sum, t) => sum + (t.globalShortSize ?? 0n), 0n);

  const v1Fees = useV1FeesInfo(BESC);
  const { moneyPrice } = useMoneyPrice(chainId);
  const { [BESC]: stakedMoneyBesc } = useTotalMoneyStaked();

  const mlpTvl = statsBesc?.mlp.aum;
  const mlpSupply = statsBesc?.reader?.tokenBalancesWithSupplies?.mlpSupply;

  const mlpPrice =
    mlpTvl !== undefined && mlpTvl > 0n && mlpSupply !== undefined
      ? bigMath.mulDiv(mlpTvl, expandDecimals(1, MLP_DECIMALS), mlpSupply)
      : expandDecimals(1, USD_DECIMALS);

  const mlpMarketCap =
    mlpSupply !== undefined
      ? bigMath.mulDiv(mlpPrice, mlpSupply, expandDecimals(1, MLP_DECIMALS))
      : undefined;

  let displayTvl: bigint | undefined = undefined;
  if (moneyPrice !== undefined && stakedMoneyBesc !== undefined && mlpMarketCap !== undefined) {
    const stakedMoneyUsd = bigMath.mulDiv(moneyPrice, stakedMoneyBesc, expandDecimals(1, MONEY_DECIMALS));
    displayTvl = stakedMoneyUsd + mlpMarketCap;
  }

  const [totalVolume, totalVolumeDelta] = useVolumeData();
  const totalDailyVolume = totalVolumeDelta ?? 0;

  const totalLongPositionSizes = totalLongOiRaw;
  const totalShortPositionSizes = totalShortOiRaw;
  const totalWeeklyFeesUsd = v1Fees?.weeklyFees;

  const dailyVolumeEntries = useMemo(
    () => ({ "BESC Hyperchain": ethers.parseUnits(totalDailyVolume.toString(), USD_DECIMALS) }),
    [totalDailyVolume]
  );

  const longPositionEntries = useMemo(
    () => ({ "BESC Hyperchain": totalLongPositionSizes }),
    [totalLongPositionSizes]
  );

  const shortPositionEntries = useMemo(
    () => ({ "BESC Hyperchain": totalShortPositionSizes }),
    [totalShortPositionSizes]
  );

  const weeklyFeesEntries = useMemo(
    () => ({ "BESC Hyperchain": totalWeeklyFeesUsd }),
    [totalWeeklyFeesUsd]
  );

  const [formattedDuration, setFormattedDuration] = useState(() => getFormattedFeesDuration());

  useEffect(() => {
    const interval = setInterval(() => {
      setFormattedDuration(getFormattedFeesDuration());
    }, 1000 * 60);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="App-card">
      <div className="App-card-title">
        <Trans>Overview</Trans>
      </div>
      <div className="App-card-divider"></div>
      <div className="App-card-content">
        <div className="App-card-row">
          <div className="label">
            <Trans>TVL</Trans>
          </div>
          <div>
            <TooltipComponent
              handle={`$${formatAmount(displayTvl, USD_DECIMALS, 0, true)}`}
              position="bottom-end"
              content={
                <>
                  <Trans>Total value locked takes into account:</Trans>
                  <br />
                  <ul className="my-8 list-disc">
                    <li className="p-2">MONEY Staked</li>
                    <li className="p-2">MLP Pool</li>
                  </ul>
                  <StatsTooltipRow label="BESC Hyperchain" value={formatAmount(displayTvl, USD_DECIMALS, 0, true)} />
                </>
              }
            />
          </div>
        </div>
        <div className="App-card-row">
          <div className="label">
            <Trans>MLP Pool</Trans>
          </div>
          <div>
            <TooltipComponent
              handle={`$${formatAmount(mlpTvl, USD_DECIMALS, 0, true)}`}
              position="bottom-end"
              content={
                <>
                  <Trans>Total value of tokens in the MLP pool.</Trans>
                  <br />
                  <br />
                  <StatsTooltipRow label="BESC Hyperchain" value={formatAmount(mlpTvl, USD_DECIMALS, 0, true)} />
                  <br />
                  <Trans>
                    This value may be higher on other websites due to the collateral of positions being included in the
                    calculation.
                  </Trans>
                </>
              }
            />
          </div>
        </div>
        <div className="App-card-row">
          <div className="label">
            <Trans>24h Volume</Trans>
          </div>
          <div>
            <TooltipComponent
              position="bottom-end"
              className="whitespace-nowrap"
              handle={`$${totalDailyVolume ? totalDailyVolume?.toFixed(2) : 0}`}
              content={
                <StatsTooltipRow
                  label="BESC Hyperchain"
                  value={formatAmount(
                    ethers.parseUnits(totalDailyVolume.toString(), USD_DECIMALS),
                    USD_DECIMALS,
                    2,
                    true
                  )}
                />
              }
            />
          </div>
        </div>
        <div className="App-card-row">
          <div className="label">
            <Trans>Open Interest</Trans>
          </div>
          <div>
            <TooltipComponent
              position="bottom-end"
              className="whitespace-nowrap"
              handle={formatAmountHuman(totalLongPositionSizes + totalShortPositionSizes, USD_DECIMALS, true, 2)}
              content={
                <>
                  <StatsTooltipRow label="Longs" value={formatAmount(totalLongPositionSizes, USD_DECIMALS, 0, true)} />
                  <StatsTooltipRow label="Shorts" value={formatAmount(totalShortPositionSizes, USD_DECIMALS, 0, true)} />
                </>
              }
            />
          </div>
        </div>
        <div className="App-card-row">
          <div className="label">
            <Trans>Long Positions</Trans>
          </div>
          <div>
            <TooltipComponent
              position="bottom-end"
              className="whitespace-nowrap"
              handle={formatAmountHuman(totalLongPositionSizes, USD_DECIMALS, true, 2)}
              content={<StatsTooltipRow label="BESC Hyperchain" value={formatAmount(totalLongPositionSizes, USD_DECIMALS, 0, true)} />}
            />
          </div>
        </div>
        <div className="App-card-row">
          <div className="label">
            <Trans>Short Positions</Trans>
          </div>
          <div>
            <TooltipComponent
              position="bottom-end"
              className="whitespace-nowrap"
              handle={formatAmountHuman(totalShortPositionSizes, USD_DECIMALS, true, 2)}
              content={<StatsTooltipRow label="BESC Hyperchain" value={formatAmount(totalShortPositionSizes, USD_DECIMALS, 0, true)} />}
            />
          </div>
        </div>
        <div className="App-card-row">
          <div className="label">
            <Trans>Fees for the past</Trans> {formattedDuration}
          </div>
          <div>
            <TooltipComponent
              position="bottom-end"
              className="whitespace-nowrap"
              handle={`$${formatAmount(totalWeeklyFeesUsd, USD_DECIMALS, 2, true)}`}
              content={<StatsTooltipRow label="BESC Hyperchain" value={formatAmount(totalWeeklyFeesUsd, USD_DECIMALS, 2, true)} />}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
